import { ArrowUpRight, Newspaper } from "lucide-react";
import { Link } from "react-router-dom";
import { CornerMarks, StatusLED } from "@/components/infra";
import QuoteCarousel from "@/components/QuoteCarousel";
import { LATEST_ARTICLE, WIRE_META } from "@/lib/wireData";

export const OUTLETS = [
  {
    name: "Yahoo Finance",
    domain: "yahoo.com",
    url: "https://finance.yahoo.com/technology/articles/data-center-youngbloods-expands-global-043200559.html",
  },
  {
    name: "AP News",
    domain: "apnews.com",
    url: "https://apnews.com/press-release/globenewswire-mobile/press-release-dbe0092891b4670630efd088239e1908",
  },
  {
    name: "Fidelity",
    domain: "fidelity.com",
    url: "https://www.fidelity.com/news/article/technology/202607110032PRIMZONEFULLFEED9760691",
  },
  {
    name: "Business Insider",
    domain: "businessinsider.com",
    url: "https://markets.businessinsider.com/news/stocks/data-center-youngbloods-expands-global-talent-pipeline-as-demand-for-digital-infrastructure-accelerates-1036316189",
  },
  {
    name: "Globe Newswire",
    domain: "globenewswire.com",
    url: "https://www.globenewswire.com/news-release/2026/07/11/3325790/0/en/data-center-youngbloods-expands-global-talent-pipeline-as-demand-for-digital-infrastructure-accelerates.html",
  },
  {
    name: "StreetInsider",
    domain: "streetinsider.com",
    url: "https://www.streetinsider.com/Globe+Newswire/Data+Center+Youngbloods+Expands+Global+Talent+Pipeline+as+Demand+for+Digital+Infrastructure+Accelerates/26758333.html",
  },
];

const LatestTag = LATEST_ARTICLE.external ? "a" : Link;

export default function InTheNews() {
  return (
    <section
      id="news"
      data-testid="in-the-news-section"
      className="border-b hairline bg-[#F4F5F7] relative overflow-hidden"
    >
      {/* Subtle scanline wash */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, #0F1117 0 1px, transparent 1px 4px)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-32">
        {/* Header */}
        <div className="grid grid-cols-12 gap-y-8 gap-x-6 mb-12 md:mb-16">
          <div className="col-span-12 lg:col-span-6">
            <div data-testid="news-section-label" className="font-mono-label text-[11px] text-[#0F1117] mb-6 flex items-center gap-3">
              <StatusLED />
              / In the news
            </div>
            <h2
              data-testid="in-the-news-headline"
              className="font-display text-3xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-extrabold text-[#0F1117] text-balance"
            >
              DCYB <span className="text-chrome">In The News.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex lg:items-end">
            <div>
              <p
                data-testid="in-the-news-subheading"
                className="text-base md:text-lg text-[#3D4049] leading-relaxed"
              >
                Coverage Across National Financial Media.
              </p>
              <div
                data-testid="news-reach-strip"
                className="mt-3 font-mono-label text-[10px] tracking-[0.16em] uppercase text-[#71747B]"
              >
                925 Outlets · 119.8M Monthly Reach · 106 Countries
              </div>
            </div>
          </div>
        </div>

        {/* Latest coverage banner — auto-derived from newest wire item */}
        <LatestTag
          {...(LATEST_ARTICLE.external
            ? { href: LATEST_ARTICLE.url, target: "_blank", rel: "noopener noreferrer" }
            : { to: LATEST_ARTICLE.url })}
          data-testid="latest-coverage-banner"
          className="group relative flex flex-col md:flex-row md:items-center gap-2.5 md:gap-6 bg-[#0F1117] px-6 md:px-8 py-5 mb-6 md:mb-8 border border-[#1A1D26] hover:border-[#4A4E58] transition-colors"
        >
          <span className="inline-flex items-center gap-2.5 shrink-0">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full bg-red-500 opacity-60" />
              <span className="relative inline-flex h-2 w-2 bg-red-500" />
            </span>
            <span className="font-mono-label text-[10px] tracking-[0.22em] uppercase text-white">
              Latest Coverage
            </span>
          </span>
          <span
            data-testid="latest-coverage-meta"
            className="font-mono-label text-[10px] tracking-[0.16em] uppercase text-[#8A8D94] shrink-0"
          >
            {LATEST_ARTICLE.date} · {LATEST_ARTICLE.outlet}
          </span>
          <span
            data-testid="latest-coverage-headline"
            className="flex-1 text-sm md:text-[15px] font-semibold text-[#D7DAE0] leading-snug group-hover:text-white transition-colors line-clamp-2 md:line-clamp-1"
          >
            {LATEST_ARTICLE.headline}
          </span>
          <ArrowUpRight
            size={16}
            className="hidden md:block shrink-0 text-[#8A8D94] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-white"
          />
        </LatestTag>

        {/* Featured story block */}
        <div
          data-testid="news-featured-story"
          className="chrome-surface relative p-7 md:p-12 mb-6 md:mb-8"
        >
          <CornerMarks color="#A9ADB4" />
          <div className="flex items-center gap-3 mb-5 md:mb-6">
            <Newspaper size={13} className="text-[#0F1117]" aria-hidden />
            <span
              data-testid="news-featured-dateline"
              className="font-mono-label text-[10.5px] tracking-[0.16em] text-[#5F6168] uppercase"
            >
              July 11, 2026 · Globe Newswire
            </span>
          </div>
          <h3
            data-testid="news-featured-headline"
            className="font-display text-xl sm:text-3xl lg:text-[42px] font-extrabold tracking-tight leading-[1.14] text-[#0F1117] text-balance max-w-4xl"
          >
            Data Center Youngbloods Expands Global{" "}
            <span className="text-chrome">Talent Pipeline</span> as Demand for
            Digital Infrastructure Accelerates.
          </h3>
        </div>

        {/* Outlet cards grid */}
        <div
          data-testid="news-outlets-grid"
          className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5"
        >
          {OUTLETS.map((outlet) => (
            <a
              key={outlet.name}
              href={outlet.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`news-card-${outlet.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative bg-white p-4 sm:p-6 md:p-7 flex flex-col justify-between min-h-[112px] sm:min-h-[148px] border border-[#D2D5DA] transition-all duration-200 hover:border-[#9498A0] hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgba(15,17,23,0.08),inset_0_1px_0_rgba(255,255,255,0.95)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1117]"
              style={{
                background: "linear-gradient(180deg, #FFFFFF 0%, #F6F7F9 100%)",
              }}
            >
              <div className="flex items-start justify-between gap-2 sm:gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0">
                  <span
                    aria-hidden
                    className="inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-md bg-white border border-[#DADCE1] shadow-[inset_0_1px_0_rgba(255,255,255,1),0_1px_1px_rgba(15,17,23,0.04)] overflow-hidden shrink-0"
                  >
                    <img
                      src={`https://www.google.com/s2/favicons?domain=${outlet.domain}&sz=128`}
                      alt=""
                      loading="lazy"
                      className="w-6 h-6 object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </span>
                  <span className="font-display text-sm min-[420px]:text-base sm:text-xl md:text-2xl font-extrabold tracking-tight text-[#0F1117] leading-tight">
                    {outlet.name}
                  </span>
                </div>
                <ArrowUpRight
                  size={16}
                  className="shrink-0 mt-1 text-[#71747B] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[#0F1117]"
                />
              </div>
              <span className="mt-4 sm:mt-6 inline-flex items-center gap-2 font-mono-label text-[9px] sm:text-[10.5px] tracking-[0.1em] sm:tracking-[0.16em] uppercase text-[#5F6168] group-hover:text-[#0F1117] transition-colors">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#22C55E]" />
                Read The Coverage
              </span>
            </a>
          ))}
        </div>

        {/* Rotating press quotes */}
        <QuoteCarousel />

        {/* Full wire CTA */}
        <div className="mt-8 md:mt-10 flex justify-center">
          <Link
            to="/wire"
            data-testid="view-full-wire-btn"
            className="group chrome-btn-dark chrome-btn-dark-glow inline-flex items-center gap-2.5 font-mono-label text-[11px] tracking-[0.16em] px-7 py-4"
          >
            <span className="live-dot" aria-hidden />
            View The Full Wire — {WIRE_META.links} Links · {WIRE_META.stories} Stories
            <ArrowUpRight
              size={15}
              className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </Link>
        </div>
      </div>
    </section>
  );
}
